import {
  IRoomValidationStatusCode,
  ITrendBucketConfig,
} from "@backend/src/shared/schemas/schemas";
import {
  ROOM_VALIDATION_STATUS_CODES,
  ROOM_VALIDATION_STATUS_LABELS,
} from "../constantsFromBackend";
import { colorSpecsToTrendBucketConfig, pickNoDataColor } from "./roomColoringHelpers";
import {
  GRAY,
  PERCENT_COLORS,
  ROOM_VALIDATION_STATUS_COLORS,
} from "./roomColoringPalettes";
import { IColorSpec, IGetColorArgs, IPolygonColors } from "./roomColoringTypes";

export type IValidationDecisionColorSpec =
  | {
      id: IRoomValidationStatusCode;
      label: string;
      fillColor: string;
    }
  | {
      id: "NO_DATA";
      label: string;
      fillColor: string;
    };

export const VALIDATION_DECISION_COLOR_SPECS: IColorSpec[] = [
  ...ROOM_VALIDATION_STATUS_CODES.map((code) => ({
    id: code,
    fillColor: ROOM_VALIDATION_STATUS_COLORS[code],
    explanation: ROOM_VALIDATION_STATUS_LABELS[code],
  })),
  {
    id: "NO_DATA",
    fillColor: GRAY,
    explanation: "Ingen status",
  },
];

export const getPercentColor = (value: number): string => {
  const p = Math.max(0, Math.min(100, Math.floor(value)));
  if (p === 100) return PERCENT_COLORS.FULL;
  if (p >= 81) return PERCENT_COLORS.P81_99;
  if (p >= 61) return PERCENT_COLORS.P61_80;
  if (p >= 41) return PERCENT_COLORS.P41_60;
  if (p >= 21) return PERCENT_COLORS.P21_40;
  if (p >= 1) return PERCENT_COLORS.P01_20;
  return PERCENT_COLORS.ZERO;
};

export const STANDARD_VALIDATION_MANUAL_SPEC_ID = "MANUAL_APPROVED";

export const RFP_VALIDATION_COLOR_SPECS: IColorSpec[] = [
  { id: "ZERO", fillColor: PERCENT_COLORS.ZERO, explanation: "0 %", range: { minInclusive: 0, maxInclusive: 0 } },
  { id: "P01_20", fillColor: PERCENT_COLORS.P01_20, explanation: "1-20 %", range: { minInclusive: 1, maxInclusive: 20 } },
  { id: "P21_40", fillColor: PERCENT_COLORS.P21_40, explanation: "21-40 %", range: { minInclusive: 21, maxInclusive: 40 } },
  { id: "P41_60", fillColor: PERCENT_COLORS.P41_60, explanation: "41-60 %", range: { minInclusive: 41, maxInclusive: 60 } },
  { id: "P61_80", fillColor: PERCENT_COLORS.P61_80, explanation: "61-80 %", range: { minInclusive: 61, maxInclusive: 80 } },
  { id: "P81_99", fillColor: PERCENT_COLORS.P81_99, explanation: "81-99 %", range: { minInclusive: 81, maxInclusive: 99 } },
  { id: "FULL", fillColor: PERCENT_COLORS.FULL, explanation: "100 %", range: { minInclusive: 100, maxInclusive: 100 } },
  { id: "NO_DATA", fillColor: GRAY, explanation: "Ingen data" },
];

export const STANDARD_VALIDATION_PERCENT_BUCKET_CONFIG: ITrendBucketConfig | undefined =
  colorSpecsToTrendBucketConfig(RFP_VALIDATION_COLOR_SPECS);

export const makeStandardRoomValidationColorFn = (key: `standardRoom.${string}`) => {
  const discipline = key.split(".")[1];

  return (args: IGetColorArgs): IPolygonColors => {
    const std = args.room?.validation?.standardRoom as
      | Record<string, { summary?: { matchRatePct?: number } }>
      | undefined;
    const approvals = args.room?.validationDecisions?.standardRoom as
      | Record<string, { approved?: boolean }>
      | undefined;

    const pct = std?.[discipline]?.summary?.matchRatePct;
    const isApproved = approvals?.[discipline]?.approved === true;
    const fill =
      pct == null || Number.isNaN(pct) ? pickNoDataColor(args, GRAY) : getPercentColor(pct);

    void isApproved;

    return { fill };
  };
};

export const makeSpecifiedArticlesValidationColorFn = (key: `specifiedArticles.${string}`) => {
  const suffix = key.split(".")[1];

  return (args: IGetColorArgs): IPolygonColors => {
    const pct =
      suffix === "overall"
        ? args.room?.validation?.specifiedArticles?.summary?.matchRatePct
        : args.room?.validation?.specifiedArticles?.perResponsibility?.[suffix]?.summary
            ?.matchRatePct;

    const fill =
      pct == null || Number.isNaN(pct) ? pickNoDataColor(args, GRAY) : getPercentColor(pct);

    return { fill };
  };
};

export const makeValidationDecisionColorFn = (decisionKey: `${string}.${string}`) => {
  const [branch, discipline] = decisionKey.split(".") as [string, string];

  return (args: IGetColorArgs): IPolygonColors => {
    const decisions = args.room?.validationDecisions;
    if (!decisions) {
      return { fill: pickNoDataColor(args, GRAY), stroke: undefined };
    }

    const record = (decisions as Record<string, unknown>)[branch] as
      | Record<string, { current?: { status?: IRoomValidationStatusCode } }>
      | undefined;

    const status = record?.[discipline]?.current?.status;
    if (!status) {
      return { fill: pickNoDataColor(args, GRAY), stroke: undefined };
    }

    return {
      fill: ROOM_VALIDATION_STATUS_COLORS[status] ?? pickNoDataColor(args, GRAY),
      stroke: undefined,
    };
  };
};
